import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDemo } from '../context/DemoContext';
import { ThemeContext } from '../context/ThemeContext';
import { ApiContext } from '../context/ApiContext';
import {
    LuMoon, LuSun, LuShieldCheck, LuLock, LuEye, LuBookOpen,
    LuCircleCheck, LuChevronDown, LuBot, LuTrendingDown,
    LuInfinity, LuCheck, LuX, LuMail, LuBell, LuUsers
} from 'react-icons/lu';
import '../styles/LandingPage.css';

const SCROLL_KEY = 'landingPageScrollY';

const LandingPage = () => {
    const navigate = useNavigate();
    const { enterDemoMode } = useDemo();
    const { theme, toggleTheme } = useContext(ThemeContext);
    const apiUrl = useContext(ApiContext);

    useEffect(() => {
        if ('scrollRestoration' in window.history) {
            window.history.scrollRestoration = 'manual';
        }
        const saved = sessionStorage.getItem(SCROLL_KEY);
        if (saved) {
            const y = parseInt(saved, 10);
            sessionStorage.removeItem(SCROLL_KEY);
            requestAnimationFrame(() => requestAnimationFrame(() => window.scrollTo(0, y)));
        }
    }, []);

    const [whyActive, setWhyActive] = useState(0);
    const [email, setEmail] = useState('');
    const [emailSubmitted, setEmailSubmitted] = useState(false);
    const [emailError, setEmailError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleEmailSubmit = async (e) => {
        e.preventDefault();
        setEmailError('');
        setIsSubmitting(true);

        try {
            // Simple email validation
            if (!email || !/^[^@]+@[^@]+\.[^@]+$/.test(email)) {
                setEmailError('Please enter a valid email address');
                return;
            }

            // Real API call to waitlist endpoint
            const response = await fetch(`${apiUrl}/api/v1/waitlist`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    email,
                    name: undefined,
                    referralSource: undefined,
                    motivation: undefined,
                }),
            });

            if (response.ok) {
                setEmailSubmitted(true);
                setTimeout(() => setEmailSubmitted(false), 3000);
            } else {
                const data = await response.json().catch(() => null);
                setEmailError(data?.message || 'Failed to submit. Please try again.');
            }
        } catch (err) {
            setEmailError('Unable to connect. Please try again later.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const whyFeatures = [
        {
            icon: <LuShieldCheck />,
            title: 'Identity-verified users',
            body: (
                <>
                    Every account on FolkPost is linked to a real, verified person. Users verify with a valid ID before joining through trusted European authorities — powered by the <a href="https://ec.europa.eu/digital-building-blocks/sites/spaces/EUDIGITALIDENTITYWALLET/pages/694487738/EU+Digital+Identity+Wallet+Home" target="_blank" rel="noopener noreferrer" style={{color: 'var(--accent)', textDecoration: 'underline', fontWeight: '600'}}>EU Digital Identity Wallet</a>, which means no anonymous bots and no fake accounts. When people are accountable for what they say, the quality of conversation changes for the better.
                </>
            ),
        },
        {
            icon: <LuLock />,
            title: 'Full privacy, zero tracking',
            body: 'Your data belongs to you. FolkPost never tracks your behavior for ad targeting and never sells your information. Full GDPR compliance means your privacy is protected by Europe\'s strongest laws — by default, not as an afterthought. We do not store any data we do not need, and we never use your data to manipulate you. On FolkPost, you are a client, not a product.',
        },
        {
            icon: <LuEye />,
            title: 'Transparent algorithms',
            body: 'You control what you see. FolkPost\'s feed algorithms are fully open and transparent — no hidden manipulation, no engagement tricks. Content is ranked by relevance and recency, not by what keeps you scrolling the longest.',
        },
        {
            icon: <LuBookOpen />,
            title: 'Mindful browsing',
            body: 'Paginated content lets you read at your own pace. There is no infinite scroll and no addictive design patterns — just a respectful experience that values your time and well-being over engagement metrics.',
        },
        {
            icon: <LuCircleCheck />,
            title: 'Fact-checking by community',
            body: 'Community notes let users add context and corrections to any post, creating a collaborative layer of trust so misinformation is caught before it spreads.',
        },
    ];

    const handleJoin = () => {
        sessionStorage.setItem(SCROLL_KEY, window.scrollY);
        navigate('/waitlist');
    };

    const handleDemo = () => {
        sessionStorage.setItem(SCROLL_KEY, window.scrollY);
        enterDemoMode();
        navigate('/posts');
    };

    return (
        <div className="LandingPage">
            {/* Theme Toggle Button */}
            <button
                className="theme-toggle-button landing-theme-toggle"
                onClick={toggleTheme}
                aria-label={theme === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}
            >
                {theme === 'light' ? <LuMoon /> : <LuSun /> }
            </button>
            
            <section className="landing-hero">
                <div className="landing-hero-content">
                    <div className="landing-logo">FolkPost</div>
                    <h1 className="landing-headline landing-headline--hero">
                        The <span style={{ color: '#f97316' }}>Rebel</span> Social Media.<br/>
                    </h1>
                    <h1 className="landing-headline" style={{ color: 'var(--accent)' }}>
                        No bots. No tracking. No infinite scroll.<br/>
                        Just real people, taking control back.<br/>
                    </h1>
                    
                    {/* Launch Notification Form */}
                    <div className="landing-launch-notification">
                        <div className="landing-launch-form-container">
                            <div className="landing-launch-form-header">
                                <LuMail className="landing-launch-form-icon" />
                                <span>Get Notified at Launch</span>
                            </div>
                            <form onSubmit={handleEmailSubmit} className="landing-launch-form">
                                <input
                                    type="email"
                                    placeholder="Enter your email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="landing-launch-input"
                                    disabled={isSubmitting}
                                />
                                <button
                                    type="submit"
                                    className="landing-launch-submit"
                                    disabled={isSubmitting}
                                >
                                    {isSubmitting ? 'Submitting...' : 'Notify Me'}
                                </button>
                            </form>
                            {emailSubmitted && (
                                <p className="landing-launch-success">Thank you! We'll notify you at launch.</p>
                            )}
                            {emailError && (
                                <p className="landing-launch-error">{emailError}</p>
                            )}
                        </div>
                    </div>
                    
                    <div className="landing-cta-group">
                        <button
                            className="landing-cta-primary"
                            onClick={handleJoin}
                        >
                            Join the Movement
                        </button>
                    </div>
                    <p className="landing-trust-note">
                        Made in Europe · Hosted in Europe
                    </p>
                </div>
                <div className="landing-scroll-hint" onClick={() => document.querySelector('.landing-manifesto')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}>
                    <span className="landing-scroll-hint-label">Rebel Manifesto</span>
                    <span className="landing-scroll-hint-arrows">
                        <LuChevronDown />
                        <LuChevronDown />
                    </span>
                </div>
            </section>

            {/* Manifesto */}
            <section className="landing-manifesto">
                <div className="landing-section">
                    <h2 className="landing-section-title">The Rebel Manifesto</h2>
                    <p className="landing-manifesto-intro">
                        We are done with the broken internet. Social media was supposed to
                        connect us, but it became a machine — feeding us bots, tracking our
                        every move, and addicting us to endless scrolls. It's time to take
                        control back.
                    </p>
                    <div className="landing-manifesto-grid">
                        <div className="landing-manifesto-grid-header"><h3>We Rebel Against</h3></div>
                        <div className="landing-manifesto-grid-header"><h3>We Stand For</h3></div>

                        <div className="landing-manifesto-grid-item"><span><LuBot /></span> Bots and fakes drowning out real voices</div>
                        <div className="landing-manifesto-grid-item"><span><LuShieldCheck /></span> Real people — every user verified, every voice accountable</div>

                        <div className="landing-manifesto-grid-item"><span><LuEye /></span> Tracking and surveillance treating us as products</div>
                        <div className="landing-manifesto-grid-item"><span><LuLock /></span> Real privacy — your data is yours. No personalized ads. No hidden agendas.</div>

                        <div className="landing-manifesto-grid-item"><span><LuInfinity /></span> Algorithmic manipulation designed to keep us hooked, not informed</div>
                        <div className="landing-manifesto-grid-item"><span><LuCircleCheck /></span> Real conversations — paginated feeds, transparent algorithms, and community-driven truth</div>
                    </div>
                    {/* <p className="landing-manifesto-closing"> */}
                    {/*     The era of cheap dopamine is over.<br/> */}
                    {/*     FolkPost is where humans reclaim the internet. */}
                    {/* </p> */}
                    <div className="landing-cta-group">
                        <button
                            className="landing-cta-primary"
                            onClick={handleJoin}
                        >
                            Join the Movement
                        </button>
                        {/* <button */}
                        {/*     className="landing-cta-secondary" */}
                        {/*     onClick={handleDemo} */}
                        {/* > */}
                        {/*     Demo */}
                        {/* </button> */}
                    </div>
                </div>
            </section>

            {/* Why FolkPost - full page */}
            <section className="landing-why">
                <div className="landing-why-inner">
                    <h2 className="landing-section-title">Why FolkPost?</h2>
                    <div className="landing-why-layout">
                        <ul className="landing-why-nav">
                            {whyFeatures.map((f, i) => (
                                <li
                                    key={i}
                                    className={whyActive === i ? 'active' : ''}
                                    onClick={() => setWhyActive(i)}
                                >
                                    <span>{f.icon}</span> {f.title}
                                </li>
                            ))}
                        </ul>
                        <div className="landing-why-detail">
                            {whyFeatures.map((feature, index) => (
                                <div
                                    key={index}
                                    className={`landing-why-detail-content ${index === whyActive ? 'active' : ''}`}
                                >
                                    <div className="landing-why-detail-header">
                                        <div className="landing-why-detail-icon">{feature.icon}</div>
                                        <h3>{feature.title}</h3>
                                    </div>
                                    <p>{feature.body}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>



            {/* Comparison table */}
            <section>
                <div className="landing-section">
                    <h2 className="landing-section-title">How we compare</h2>
                    <p className="landing-section-subtitle">
                        FolkPost is the only platform that combines all of these safeguards.
                    </p>
                    <div className="landing-comparison">
                        <table>
                            <thead>
                                <tr>
                                    <th>Feature</th>
                                    <th className="landing-highlight-col">FolkPost</th>
                                    <th>Twitter/X</th>
                                    <th>Facebook</th>
                                    <th>Mastodon</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td>Identity verification</td>
                                    <td className="landing-highlight-col"><LuCheck /> Required</td>
                                    <td><LuX /></td>
                                    <td><LuX /></td>
                                    <td><LuX /></td>
                                </tr>
                                <tr>
                                    <td>Community fact-checking</td>
                                    <td className="landing-highlight-col"><LuCheck /> Integrated</td>
                                    <td><LuX /></td>
                                    <td>Limited</td>
                                    <td><LuX /></td>
                                </tr>
                                <tr>
                                    <td>Community Notes</td>
                                    <td className="landing-highlight-col"><LuCheck /> Integrated</td>
                                    <td><LuCheck /> Community Notes</td>
                                    <td>Limited (flagging)</td>
                                    <td><LuX /></td>
                                </tr>
                                <tr>
                                    <td>No infinite scroll</td>
                                    <td className="landing-highlight-col"><LuCheck /> Pagination</td>
                                    <td><LuX /></td>
                                    <td><LuX /></td>
                                    <td><LuX /></td>
                                </tr>
                                <tr>
                                    <td>Ad-free option</td>
                                    <td className="landing-highlight-col"><LuCheck /></td>
                                    <td><LuX /></td>
                                    <td><LuX /></td>
                                    <td><LuCheck /></td>
                                </tr>
                                <tr>
                                    <td>GDPR / DSA compliant</td>
                                    <td className="landing-highlight-col"><LuCheck /> Full</td>
                                    <td>Partial</td>
                                    <td>Partial</td>
                                    <td><LuCheck /></td>
                                </tr>
                                <tr>
                                    <td>Algorithm transparency</td>
                                    <td className="landing-highlight-col"><LuCheck /> Full</td>
                                    <td><LuX /></td>
                                    <td><LuX /></td>
                                    <td>Partial</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </section>

            {/* Pricing */}
            <section className="landing-pricing">
                <div className="landing-pricing-inner">
                    <h2 className="landing-section-title">Simple, Honest Pricing</h2>
                    <p className="landing-section-subtitle">
                        We don't sell your data. We don't run targeted ads. We charge a fair price for a service worth paying for.
                    </p>
                    <div className="landing-pricing-cards">
                        <div className="landing-pricing-card landing-pricing-card--featured">
                            <div className="landing-pricing-card-header">
                                <span className="landing-pricing-badge landing-pricing-badge--accent">Member</span>
                                <div className="landing-pricing-amount">
                                    <span className="landing-pricing-price">Paid</span>
                                </div>
                            </div>
                            <p className="landing-pricing-desc">Full access. Your data stays yours. Always.</p>
                            <ul className="landing-pricing-features">
                                <li>Unlimited posts &amp; replies</li>
                                <li>Full privacy controls</li>
                                <li>No targeted ads, no tracking</li>
                                <li>EU-hosted infrastructure</li>
                                <li>Support a fair web</li>
                            </ul>
                            <p className="landing-pricing-eu-note">
                                Pricing is based on EU Digital Identity Wallet and hosting service costs, and will be announced at launch.
                            </p>
                        </div>
                    </div>
                    <p className="landing-pricing-note">
                        Your subscription funds the platform. Just a service that works for you.
                    </p>
                    <div className="landing-cta-group">
                        <button
                            className="landing-cta-primary"
                            onClick={handleJoin}
                        >
                            Join the Waitlist
                        </button>
                        <button
                            className="landing-cta-secondary"
                            onClick={handleDemo}
                        >
                            Try Demo
                        </button>
                    </div>
                </div>
            </section>

            {/* Bottom CTA */}
            {/* <section className="landing-bottom-cta"> */}
            {/*     <h2>Ready for a platform you can trust?</h2> */}
            {/* </section> */}

            {/* Footer */}
            <footer className="landing-footer">
                {/* © {new Date().getFullYear()} FolkPost. All rights reserved. */}
                {new Date().getFullYear()} FolkPost.
                <span className="landing-footer-sep">·</span>
                <button className="landing-footer-link" onClick={() => navigate('/contact-us')}>Contact Us</button>
            </footer>
        </div>
    );
};

export default LandingPage;
